/**
 * Table Node Converter
 *
 * Handles conversion of ProseMirror table nodes to TableBlocks
 */

import type {
  BorderSpec,
  BoxSpacing,
  CellBorders,
  CellSpacing,
  FlowBlock,
  ParagraphBlock,
  ImageBlock,
  DrawingBlock,
  TableCell,
  TableCellAttrs,
  TableBorders,
  TableRow,
  TableRowAttrs,
  TableBlock,
  TableAnchor,
  TableWrap,
  SourceAnchor,
  TrackedChangeMeta,
} from '@superdoc/contracts';
import type {
  PMNode,
  NodeHandlerContext,
  BlockIdGenerator,
  PositionMap,
  TrackedChangesConfig,
  HyperlinkConfig,
  ThemeColorPalette,
  ConverterContext,
  NestedConverters,
  TableNodeToBlockParams,
} from '../types.js';
import {
  extractTableBorders,
  extractCellBorders,
  extractCellPadding,
  convertBorderSpec,
  normalizeShadingColor,
  borderSizeToPx,
} from '../attributes/index.js';
import { pickNumber, twipsToPx } from '../utilities.js';
import { hydrateTableStyleAttrs } from './table-styles.js';
import { resolveShadingFillColor } from '@converter/helpers.js';
import { collectTrackedChangeFromMarks } from '../marks/index.js';
import { annotateBlockWithTrackedChange, shouldHideTrackedNode, deriveTrackedChangeId } from '../tracked-changes.js';
import {
  resolveNodeSdtMetadata,
  applySdtMetadataToParagraphBlocks,
  applySdtMetadataToTableBlock,
} from '../sdt/index.js';
import {
  TableProperties,
  resolveTableCellProperties,
  resolveExistingTableEffectiveStyleId,
  type TableInfo,
} from '@superdoc/style-engine/ooxml';
import { resolveThemeColorValue } from '../marks/theme-color.js';
import { resolveTableDirection, resolveSectionDirection } from '../direction/index.js';
import { hydrateTextboxDrawingContent } from './shapes.js';

/**
 * Normalizes tableCellSpacing from PM node to CellSpacing object format.
 * Converts legacy number values (pixels) to { value, type: 'px' } so that
 * FlowBlock table attrs always use the object format and deserialization is safe.
 */
function normalizeCellSpacing(raw: number | { value?: number; type?: string } | null | undefined): CellSpacing {
  if (raw == null) {
    return { value: 0, type: 'px' };
  }
  if (typeof raw === 'number') {
    return { value: Math.max(0, raw), type: 'px' };
  }
  const value = typeof raw.value === 'number' ? Math.max(0, raw.value) : 0;
  const t = (raw.type ?? 'px').toLowerCase();
  const type = t === 'dxa' ? 'dxa' : 'px';
  return { value, type };
}

function sourceAnchorFromNode(node: PMNode): SourceAnchor | undefined {
  const sourceAnchor = (node.attrs as Record<string, unknown> | undefined)?.sourceAnchor;
  return sourceAnchor && typeof sourceAnchor === 'object' && !Array.isArray(sourceAnchor)
    ? (sourceAnchor as SourceAnchor)
    : undefined;
}

type TableParserDependencies = {
  nextBlockId: BlockIdGenerator;
  positions: PositionMap;
  storyKey?: string;
  trackedChangesConfig: TrackedChangesConfig;
  bookmarks: Map<string, number>;
  hyperlinkConfig: HyperlinkConfig;
  themeColors?: ThemeColorPalette;
  converterContext: ConverterContext;
  converters: NestedConverters;
  enableComments: boolean;
};

type ParseTableCellArgs = {
  cellNode: PMNode;
  rowIndex: number;
  cellIndex: number;
  numCells: number;
  numRows: number;
  context: TableParserDependencies;
  defaultCellPadding?: BoxSpacing;
  tableProperties?: TableProperties;
  rowCnfStyle?: Record<string, unknown> | null;
  /** Grid placement for conditional style regions (SD-3028 G7). */
  gridPlacement?: GridCellPlacement | null;
  /** Total grid columns in the table (w:tblGrid length). */
  numGridCols?: number;
};

type ParseTableRowArgs = {
  rowNode: PMNode;
  rowIndex: number;
  numRows: number;
  context: TableParserDependencies;
  defaultCellPadding?: BoxSpacing;
  /** Table style to pass to paragraph converter for style cascade */
  tableProperties?: TableProperties;
  /** Per-content-index grid placements for this row (SD-3028 G7). */
  cellGridPlacements?: Array<GridCellPlacement | null>;
  /** Total grid columns in the table (w:tblGrid length). */
  numGridCols?: number;
};

/** Grid column placement of one display cell (SD-3028 G7). */
type GridCellPlacement = {
  gridColumnStart: number;
  gridColumnSpan: number;
};

/**
 * Place a row's cells on the table grid (SD-3028 G7).
 *
 * Word's firstCol/lastCol/banding conditional regions follow GRID columns, but
 * the PM document only exposes display cells: vMerge continuations are merged
 * into rowspans on earlier rows and gridBefore/gridAfter become placeholder
 * cells. This walks the row's content with a column cursor, skipping columns
 * occupied by rowspans from above, so every display cell knows its grid start.
 *
 * Mirrors the measuring normalizer's activeRowSpans idiom: `activeRowSpans[col]`
 * counts how many upcoming rows column `col` is still covered by.
 *
 * @param rowNode - The PM table row node.
 * @param activeRowSpans - Occupied-column counters carried from previous rows.
 * @returns Placements aligned to `rowNode.content` indices plus the counters
 *   for the next row.
 */
const placeRowCellsOnGrid = (
  rowNode: PMNode,
  activeRowSpans: number[],
): { placements: Array<GridCellPlacement | null>; nextActiveRowSpans: number[] } => {
  const placements: Array<GridCellPlacement | null> = [];
  const nextActiveRowSpans = activeRowSpans.map((count) => Math.max(0, count - 1));
  let column = 0;

  const cellSpan = (cellNode: PMNode): number => {
    const colspan = cellNode.attrs?.colspan;
    if (typeof colspan === 'number' && colspan > 0) return colspan;
    const colwidth = cellNode.attrs?.colwidth;
    return Array.isArray(colwidth) && colwidth.length > 0 ? colwidth.length : 1;
  };

  for (const cellNode of Array.isArray(rowNode.content) ? rowNode.content : []) {
    if (!isTableCellNode(cellNode)) {
      placements.push(null);
      continue;
    }
    while ((activeRowSpans[column] ?? 0) > 0) column += 1;
    const span = cellSpan(cellNode);
    placements.push({ gridColumnStart: column, gridColumnSpan: span });
    const rowspan = typeof cellNode.attrs?.rowspan === 'number' ? cellNode.attrs.rowspan : 1;
    if (rowspan > 1) {
      for (let covered = column; covered < column + span; covered += 1) {
        nextActiveRowSpans[covered] = Math.max(nextActiveRowSpans[covered] ?? 0, rowspan - 1);
      }
    }
    column += span;
  }

  return { placements, nextActiveRowSpans };
};

const isTableRowNode = (node: PMNode): boolean => node.type === 'tableRow' || node.type === 'table_row';

const isTableCellNode = (node: PMNode): boolean =>
  node.type === 'tableCell' ||
  node.type === 'table_cell' ||
  node.type === 'tableHeader' ||
  node.type === 'table_header';

const isTableSkipPlaceholderCell = (node: PMNode): boolean => {
  const placeholder = node.attrs?.__placeholder;
  return placeholder === 'gridBefore' || placeholder === 'gridAfter';
};

// Legacy persisted-cell borders sometimes use lowercase or alias forms
// (`dot`, `dotdash`, `doublewave`) instead of the canonical camelCase
// BorderStyle enum. convertBorderSpec passes `val` through unchanged, so
// the legacy fallback path normalizes here before handoff.
function normalizeLegacyBorderStyle(value: string | undefined): string {
  switch ((value ?? '').trim().toLowerCase()) {
    case 'none':
    case 'nil':
      return 'none';
    case 'double':
      return 'double';
    case 'dashed':
      return 'dashed';
    case 'dotted':
    case 'dot':
      return 'dotted';
    case 'thick':
      return 'thick';
    case 'triple':
      return 'triple';
    case 'dotdash':
      return 'dotDash';
    case 'dotdotdash':
      return 'dotDotDash';
    case 'dashsmallgap':
      return 'dashSmallGap';
    case 'thinthicksmallgap':
      return 'thinThickSmallGap';
    case 'thickthinsmallgap':
      return 'thickThinSmallGap';
    case 'thinthickthinsmallgap':
      return 'thinThickThinSmallGap';
    case 'thinthickmediumgap':
      return 'thinThickMediumGap';
    case 'thickthinmediumgap':
      return 'thickThinMediumGap';
    case 'thinthickthinmediumgap':
      return 'thinThickThinMediumGap';
    case 'thinthicklargegap':
      return 'thinThickLargeGap';
    case 'thickthinlargegap':
      return 'thickThinLargeGap';
    case 'thinthickthinlargegap':
      return 'thinThickThinLargeGap';
    case 'wave':
      return 'wave';
    case 'doublewave':
      return 'doubleWave';
    case 'dashdotstroked':
      return 'dashDotStroked';
    case 'threedemboss':
      return 'threeDEmboss';
    case 'threedengrave':
      return 'threeDEngrave';
    case 'outset':
      return 'outset';
    case 'inset':
      return 'inset';
    case 'single':
    default:
      return 'single';
  }
}

const convertResolvedCellBorder = (value: unknown): BorderSpec | undefined => {
  if (!value || typeof value !== 'object') return undefined;

  const border = value as Record<string, unknown>;
  const size = typeof border.size === 'number' ? borderSizeToPx(border.size) : undefined;
  const normalized = size != null ? { ...border, size } : border;

  return convertBorderSpec(normalized);
};

type NormalizedRowHeight =
  | {
      value: number;
      rule: 'exact' | 'atLeast' | 'auto';
    }
  | undefined;

/**
 * Normalize row height from DOCX row properties, converting from twips to pixels.
 *
 * Extracts the row height value and rule from OOXML table row properties and converts
 * the height value from twips (twentieth of a point) to pixels for consistent rendering.
 * This conversion is critical to prevent small twips values (e.g., 277 twips ≈ 18.5px)
 * from being misinterpreted as large pixel values.
 *
 * @param rowProps - Table row properties object containing optional rowHeight configuration
 * @returns Normalized height object with pixel value and rule, or undefined if no valid height is found
 *
 * @example
 * // DOCX row with exact height of 277 twips
 * const props = { rowHeight: { value: 277, rule: 'exact' } };
 * const normalized = normalizeRowHeight(props);
 * // Returns: { value: 18.467, rule: 'exact' }
 *
 * @example
 * // Missing or invalid height
 * normalizeRowHeight(undefined); // Returns: undefined
 * normalizeRowHeight({}); // Returns: undefined
 */
const normalizeRowHeight = (rowProps?: Record<string, unknown>): NormalizedRowHeight => {
  if (!rowProps || typeof rowProps !== 'object') return undefined;
  const rawRowHeight = (rowProps as Record<string, unknown>).rowHeight;
  if (!rawRowHeight || typeof rawRowHeight !== 'object') return undefined;

  const heightObj = rawRowHeight as Record<string, unknown>;
  const rawValue = pickNumber(heightObj.value ?? heightObj.val);
  if (rawValue == null) return undefined;

  const rawRule = heightObj.rule ?? heightObj.hRule;
  const rule =
    rawRule === 'exact' || rawRule === 'atLeast' || rawRule === 'auto'
      ? (rawRule as 'exact' | 'atLeast' | 'auto')
      : 'atLeast';

  // Row heights from DOCX are defined in twips. Always convert to px so small values (e.g. 277 twips)
  // don't get misinterpreted as pixels.
  const valuePx = twipsToPx(rawValue);

  return {
    value: valuePx,
    rule,
  };
};

/**
 * Parse a ProseMirror table cell node into a TableCell block.
 *
 * Converts a PM table cell node (tableCell, table_cell, tableHeader, or table_header)
 * into the SuperDoc TableCell contract format. Processes all paragraphs within the cell,
 * extracts cell attributes (borders, padding, alignment, background), and handles
 * merged cells (rowspan/colspan).
 *
 * @param args - Cell parsing arguments including node, position, context, and style cascade props
 * @param args.cellNode - ProseMirror cell node to parse
 * @param args.rowIndex - Zero-based row index for ID generation
 * @param args.cellIndex - Zero-based cell index for ID generation
 * @param args.context - Parser dependencies (block ID generator, converters, style context)
 * @param args.defaultCellPadding - Optional default padding from table style
 * @param args.tableStyleParagraphProps - Optional paragraph properties from table style for cascade
 * @returns TableCell object with blocks and attributes, or null if the cell is invalid or empty
 *
 * @example
 * // Valid cell with content
 * const cell = parseTableCell({
 *   cellNode: { type: 'tableCell', content: [paragraphNode] },
 *   rowIndex: 0,
 *   cellIndex: 1,
 *   context: parserDeps,
 * });
 * // Returns: { id: 'cell-0-1', blocks: [...], attrs: {...} }
 *
 * @example
 * // Empty cell returns null
 * parseTableCell({
 *   cellNode: { type: 'tableCell', content: [] },
 *   rowIndex: 0,
 *   cellIndex: 0,
 *   context: parserDeps,
 * });
 * // Returns: null
 */
const parseTableCell = (args: ParseTableCellArgs): TableCell | null => {
  const { cellNode, rowIndex, cellIndex, numCells, numRows, context, defaultCellPadding, tableProperties } = args;
  if (!isTableCellNode(cellNode) || !Array.isArray(cellNode.content)) {
    return null;
  }

  // Convert all cell children into blocks.
  // Table cells can contain paragraphs, images/drawings, structured content blocks, and nested tables.
  const blocks: (ParagraphBlock | ImageBlock | DrawingBlock | TableBlock)[] = [];

  // Build tableInfo once with cnfStyle flags and reuse for both cascade and context.
  const rowCnfStyle = args.rowCnfStyle ?? null;
  const cellCnfStyle = (cellNode.attrs?.tableCellProperties as Record<string, unknown> | undefined)?.cnfStyle ?? null;
  const tableInfo: TableInfo | undefined = tableProperties
    ? {
        tableProperties,
        rowIndex,
        cellIndex,
        numCells,
        numRows,
        rowCnfStyle,
        cellCnfStyle,
        ...(args.gridPlacement != null && args.numGridCols != null
          ? {
              gridColumnStart: args.gridPlacement.gridColumnStart,
              gridColumnSpan: args.gridPlacement.gridColumnSpan,
              numGridCols: args.numGridCols,
            }
          : {}),
      }
    : undefined;

  // Resolve table cell properties from the style cascade (wholeTable → bands → conditional → inline)
  const inlineTcProps = cellNode.attrs?.tableCellProperties as Record<string, unknown> | undefined;
  const resolvedTcProps = resolveTableCellProperties(
    inlineTcProps as Parameters<typeof resolveTableCellProperties>[0],
    tableInfo,
    context.converterContext?.translatedLinkedStyles,
  );

  // Extract cell background color for auto text color resolution
  // Priority: inline background attr > literal fill > theme fill
  const cellBackground = cellNode.attrs?.background as { color?: string } | undefined;
  let cellBackgroundColor: string | undefined;
  if (cellBackground && typeof cellBackground.color === 'string') {
    const rawColor = cellBackground.color.trim();
    if (rawColor) {
      const normalized = rawColor.startsWith('#') ? rawColor : `#${rawColor}`;
      // Validate it's a proper hex color (3 or 6 hex digits after #)
      if (/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(normalized)) {
        cellBackgroundColor = normalized;
      }
    }
  }
  // Fall back to resolved style shading if no inline background. Resolve the fill
  // base (explicit hex → theme fill), then apply the OOXML shading pattern
  // (pctNN/solid) via the shared resolver so a table style's `pct10`/`auto` shading
  // (e.g. a tblStylePr firstRow band) becomes the gray Word paints, instead of being
  // dropped. (SD-2969)
  if (!cellBackgroundColor && resolvedTcProps?.shading) {
    const { fill, color, val, themeFill, themeFillTint, themeFillShade } = resolvedTcProps.shading;
    let fillBase = normalizeShadingColor(fill);
    if (!fillBase && themeFill && context.themeColors) {
      const resolvedTheme = resolveThemeColorValue(themeFill, themeFillTint, themeFillShade, context.themeColors);
      fillBase = normalizeShadingColor(resolvedTheme);
    }
    const resolved = resolveShadingFillColor({ val, color, fill: fillBase ?? fill });
    if (resolved) {
      cellBackgroundColor = resolved.startsWith('#') ? resolved : `#${resolved}`;
    }
  }

  // Create enhanced converter context with table style paragraph props for the style cascade
  // This allows paragraphs inside table cells to inherit table style's pPr
  // Also includes backgroundColor for auto text color resolution
  const cellConverterContext: ConverterContext =
    tableInfo || cellBackgroundColor
      ? ({
          ...context.converterContext,
          ...(tableInfo && { tableInfo }),
          ...(cellBackgroundColor && { backgroundColor: cellBackgroundColor }),
        } as ConverterContext)
      : context.converterContext;

  const paragraphToFlowBlocks = context.converters.paragraphToFlowBlocks;
  const tableNodeToBlock = context.converters?.tableNodeToBlock;

  /**
   * Appends converted paragraph blocks to the cell's blocks array.
   *
   * This helper:
   * 1. Applies SDT metadata to paragraph blocks (for structured content inheritance)
   * 2. Filters to only include supported block types (paragraph, image, drawing)
   * 3. Appends the filtered blocks to the cell's blocks array
   *
   * @param paragraphBlocks - The converted flow blocks from a paragraph node
   * @param sdtMetadata - Optional SDT metadata to apply (from parent structuredContentBlock)
   */
  const appendParagraphBlocks = (
    paragraphBlocks: FlowBlock[],
    sdtMetadata?: ReturnType<typeof resolveNodeSdtMetadata>,
  ) => {
    applySdtMetadataToParagraphBlocks(
      paragraphBlocks.filter((block) => block.kind === 'paragraph') as ParagraphBlock[],
      sdtMetadata,
    );
    paragraphBlocks.forEach((block) => {
      if (block.kind === 'paragraph' || block.kind === 'image' || block.kind === 'drawing') {
        blocks.push(block);
      }
    });
  };

  // SDT wrappers (documentPartObject, structuredContentBlock) can nest
  // arbitrarily deep around the visible paragraph/table content.
  const flattenSdtWrappersIntoCell = (
    wrapperNode: PMNode,
    inheritedSdtMetadata: ReturnType<typeof resolveNodeSdtMetadata> | undefined,
  ): void => {
    if (!Array.isArray(wrapperNode.content)) return;
    for (const nestedNode of wrapperNode.content) {
      if (nestedNode.type === 'paragraph') {
        if (!paragraphToFlowBlocks) continue;
        const paragraphBlocks = paragraphToFlowBlocks({
          para: nestedNode,
          nextBlockId: context.nextBlockId,
          positions: context.positions,
          storyKey: context.storyKey,
          trackedChangesConfig: context.trackedChangesConfig,
          bookmarks: context.bookmarks,
          hyperlinkConfig: context.hyperlinkConfig,
          themeColors: context.themeColors,
          converterContext: cellConverterContext,
          converters: context.converters,
          enableComments: context.enableComments,
        });
        appendParagraphBlocks(paragraphBlocks, inheritedSdtMetadata);
        continue;
      }
      if (nestedNode.type === 'table' && tableNodeToBlock) {
        const tableBlock = tableNodeToBlock(nestedNode, {
          nextBlockId: context.nextBlockId,
          positions: context.positions,
          storyKey: context.storyKey,
          trackedChangesConfig: context.trackedChangesConfig,
          bookmarks: context.bookmarks,
          hyperlinkConfig: context.hyperlinkConfig,
          themeColors: context.themeColors,
          converterContext: context.converterContext,
          converters: context.converters,
          enableComments: context.enableComments,
        });
        if (tableBlock && tableBlock.kind === 'table') {
          if (inheritedSdtMetadata) {
            applySdtMetadataToTableBlock(tableBlock, inheritedSdtMetadata);
          }
          blocks.push(tableBlock);
        }
        continue;
      }
      if (nestedNode.type === 'documentPartObject') {
        flattenSdtWrappersIntoCell(nestedNode, inheritedSdtMetadata);
        continue;
      }
      if (nestedNode.type === 'structuredContentBlock') {
        const innerMetadata = inheritedSdtMetadata ?? resolveNodeSdtMetadata(nestedNode, 'structuredContentBlock');
        flattenSdtWrappersIntoCell(nestedNode, innerMetadata);
        continue;
      }
    }
  };

  for (const childNode of cellNode.content) {
    if (childNode.type === 'paragraph') {
      if (!paragraphToFlowBlocks) continue;
      const paragraphBlocks = paragraphToFlowBlocks({
        para: childNode,
        nextBlockId: context.nextBlockId,
        positions: context.positions,
        storyKey: context.storyKey,
        trackedChangesConfig: context.trackedChangesConfig,
        bookmarks: context.bookmarks,
        hyperlinkConfig: context.hyperlinkConfig,
        themeColors: context.themeColors,
        converterContext: cellConverterContext,
        converters: context.converters,
        enableComments: context.enableComments,
      });
      appendParagraphBlocks(paragraphBlocks);
      continue;
    }

    if (childNode.type === 'structuredContentBlock' && Array.isArray(childNode.content)) {
      const structuredContentMetadata = resolveNodeSdtMetadata(childNode, 'structuredContentBlock');
      flattenSdtWrappersIntoCell(childNode, structuredContentMetadata);
      continue;
    }

    if (childNode.type === 'table' && tableNodeToBlock) {
      const tableBlock = tableNodeToBlock(childNode, {
        nextBlockId: context.nextBlockId,
        positions: context.positions,
        storyKey: context.storyKey,
        trackedChangesConfig: context.trackedChangesConfig,
        bookmarks: context.bookmarks,
        hyperlinkConfig: context.hyperlinkConfig,
        themeColors: context.themeColors,
        converterContext: context.converterContext,
        converters: context.converters,
        enableComments: context.enableComments,
      });
      if (tableBlock && tableBlock.kind === 'table') {
        blocks.push(tableBlock);
      }
      continue;
    }

    // SD-2516: documentPartObject is a transparent wrapper; flatten its
    // (possibly nested) paragraph/table leaves into the cell.
    if (childNode.type === 'documentPartObject' && Array.isArray(childNode.content)) {
      flattenSdtWrappersIntoCell(childNode, undefined);
      continue;
    }

    if (childNode.type === 'image' && context.converters?.imageNodeToBlock) {
      const mergedMarks = [...(childNode.marks ?? [])];
      const trackedMeta = context.trackedChangesConfig
        ? collectTrackedChangeFromMarks(mergedMarks, context.storyKey)
        : undefined;
      if (shouldHideTrackedNode(trackedMeta, context.trackedChangesConfig)) {
        continue;
      }
      const imageBlock = context.converters.imageNodeToBlock(
        childNode,
        context.nextBlockId,
        context.positions,
        trackedMeta,
        context.trackedChangesConfig,
      );
      if (imageBlock && imageBlock.kind === 'image') {
        annotateBlockWithTrackedChange(imageBlock, trackedMeta, context.trackedChangesConfig);
        blocks.push(imageBlock);
      }
      continue;
    }

    if (childNode.type === 'vectorShape' && context.converters?.vectorShapeNodeToDrawingBlock) {
      const drawingBlock = context.converters.vectorShapeNodeToDrawingBlock(
        childNode,
        context.nextBlockId,
        context.positions,
      );
      if (drawingBlock && drawingBlock.kind === 'drawing') {
        blocks.push(drawingBlock);
      }
      continue;
    }

    if (childNode.type === 'shapeGroup' && context.converters?.shapeGroupNodeToDrawingBlock) {
      const drawingBlock = context.converters.shapeGroupNodeToDrawingBlock(
        childNode,
        context.nextBlockId,
        context.positions,
      );
      if (drawingBlock && drawingBlock.kind === 'drawing') {
        blocks.push(drawingBlock);
      }
      continue;
    }

    if (childNode.type === 'shapeContainer' && context.converters?.shapeContainerNodeToDrawingBlock) {
      const drawingBlock = context.converters.shapeContainerNodeToDrawingBlock(
        childNode,
        context.nextBlockId,
        context.positions,
      );
      if (drawingBlock && drawingBlock.kind === 'drawing') {
        blocks.push(hydrateTextboxDrawingContent(childNode, drawingBlock, context));
      }
      continue;
    }

    if (childNode.type === 'shapeTextbox' && context.converters?.shapeTextboxNodeToDrawingBlock) {
      const drawingBlock = context.converters.shapeTextboxNodeToDrawingBlock(
        childNode,
        context.nextBlockId,
        context.positions,
      );
      if (drawingBlock && drawingBlock.kind === 'drawing') {
        blocks.push(hydrateTextboxDrawingContent(childNode, drawingBlock, context));
      }
      continue;
    }
  }

  if (blocks.length === 0) {
    return null;
  }

  const cellAttrs: TableCellAttrs = {};

  // Cell borders come from the style-engine cascade (resolvedTcProps.borders).
  // Inline tableCellProperties.borders are already folded into resolvedTcProps
  // by resolveTableCellProperties (inline wins over style cascade).
  if (resolvedTcProps?.borders && typeof resolvedTcProps.borders === 'object') {
    const resolvedBordersData = resolvedTcProps.borders as Record<string, unknown>;
    const resolvedBorders: CellBorders = {};
    for (const side of ['top', 'right', 'bottom', 'left'] as const) {
      const spec = convertResolvedCellBorder(resolvedBordersData[side]);
      if (spec) resolvedBorders[side] = spec;
    }
    // Logical start/end fallback (LTR-default). The painter's
    // swapCellBordersLR is the single source of the RTL visual mirror
    // for cell borders (§17.4.33/12). Pre-swapping here would double-mirror.
    if (resolvedBorders.left == null) {
      const spec = convertResolvedCellBorder(resolvedBordersData.start);
      if (spec) resolvedBorders.left = spec;
    }
    if (resolvedBorders.right == null) {
      const spec = convertResolvedCellBorder(resolvedBordersData.end);
      if (spec) resolvedBorders.right = spec;
    }
    if (Object.keys(resolvedBorders).length > 0) {
      cellAttrs.borders = resolvedBorders;
    }
  }

  // Fallback: older persisted docs may store cell borders in attrs.borders
  // (pre-migration pixel format: { size: px, color: hex, val: string }).
  // The transaction-based migration only runs when an edit touches the table
  // range, so untouched legacy cells need this fallback for rendering.
  // Only borders with a `val` property qualify — old schema defaults from
  // createCellBorders() lack `val` and should be ignored (the style-engine
  // resolves those from the table style cascade).
  if (!cellAttrs.borders && cellNode.attrs?.borders && typeof cellNode.attrs.borders === 'object') {
    const legacy = cellNode.attrs.borders as Record<string, { size?: number; color?: string; val?: string }>;
    const filteredLegacyBorders: Record<string, unknown> = {};
    for (const side of ['top', 'right', 'bottom', 'left', 'start', 'end'] as const) {
      const b = legacy[side];
      if (b && b.val && typeof b.size === 'number' && b.size > 0) {
        // Legacy persisted docs may store lowercase or alias forms (`dot`,
        // `dotdash`, `doublewave`) instead of the camelCase BorderStyle enum.
        // Normalize before handing to extractCellBorders so convertBorderSpec
        // doesn't pass a non-canonical string through to the painter.
        filteredLegacyBorders[side] = { ...b, val: normalizeLegacyBorderStyle(b.val) };
      }
    }
    const fallback = extractCellBorders({ borders: filteredLegacyBorders });
    if (fallback) {
      cellAttrs.borders = fallback;
    }
  }

  const padding =
    extractCellPadding(cellNode.attrs ?? {}) ?? (defaultCellPadding ? { ...defaultCellPadding } : undefined);
  if (padding) cellAttrs.padding = padding;

  const verticalAlign = cellNode.attrs?.verticalAlign;
  const normalizedVerticalAlign =
    verticalAlign === 'middle' ? 'center' : verticalAlign === 'center' ? 'center' : verticalAlign;
  if (
    normalizedVerticalAlign === 'top' ||
    normalizedVerticalAlign === 'center' ||
    normalizedVerticalAlign === 'bottom'
  ) {
    cellAttrs.verticalAlign = normalizedVerticalAlign;
  }

  const background = cellNode.attrs?.background as { color?: string } | undefined;
  if (background && typeof background.color === 'string') {
    const bgColor = background.color;
    cellAttrs.background = bgColor.startsWith('#') ? bgColor : `#${bgColor}`;
  } else if (cellBackgroundColor) {
    // Use resolved style background when no inline background is set
    cellAttrs.background = cellBackgroundColor;
  }

  const tableCellProperties = cellNode.attrs?.tableCellProperties;
  if (tableCellProperties && typeof tableCellProperties === 'object') {
    cellAttrs.tableCellProperties = tableCellProperties as Record<string, unknown>;
  }

  const rowSpan = pickNumber(cellNode.attrs?.rowspan);
  const colSpan = pickNumber(cellNode.attrs?.colspan);

  return {
    id: context.nextBlockId(`cell-${rowIndex}-${cellIndex}`),
    blocks,
    // Backward compatibility: set paragraph to first block if it's a paragraph
    paragraph: blocks[0]?.kind === 'paragraph' ? (blocks[0] as ParagraphBlock) : undefined,
    rowSpan: rowSpan ?? undefined,
    colSpan: colSpan ?? undefined,
    attrs: Object.keys(cellAttrs).length > 0 ? cellAttrs : undefined,
    sourceAnchor: sourceAnchorFromNode(cellNode),
  };
};

/**
 * Parse a ProseMirror table row node into a TableRow block.
 *
 * Converts a PM table row node (tableRow or table_row) into the SuperDoc TableRow
 * contract format. Processes all table cells within the row, extracts row attributes
 * (row height with twips-to-pixels conversion), and preserves OOXML table row properties.
 *
 * @param args - Row parsing arguments including node, position, context, and style cascade props
 * @param args.rowNode - ProseMirror row node to parse
 * @param args.rowIndex - Zero-based row index for ID generation
 * @param args.context - Parser dependencies (block ID generator, converters, style context)
 * @param args.defaultCellPadding - Optional default padding from table style to pass to cells
 * @param args.tableStyleId - Optional table style ID for paragraph style cascade in cells
 * @returns TableRow object with cells and attributes, including structurally empty rowspan continuation rows,
 *   or null when the node is not a table row
 *
 * @example
 * // Row with cells
 * const row = parseTableRow({
 *   rowNode: { type: 'tableRow', content: [cellNode1, cellNode2] },
 *   rowIndex: 0,
 *   context: parserDeps,
 * });
 * // Returns: { id: 'row-0', cells: [...], attrs: {...} }
 *
 * @example
 * // A structurally empty row is preserved. ProseMirror emits these rows when
 * // a full-width cell spans vertically across them.
 * parseTableRow({
 *   rowNode: { type: 'tableRow', content: [] },
 *   rowIndex: 0,
 *   context: parserDeps,
 * });
 * // Returns: { id: 'row-0', cells: [] }
 */
/**
 * Builds shared {@link TrackedChangeMeta} for a structural row-level tracked
 * change (inserted/deleted whole row) from the PM `tableRow` node's
 * `attrs.trackChange`. Mirrors the inline mark conversion in
 * `layout-adapter/tracked-changes.ts` (`buildTrackedChangeMetaFromMark`) and
 * reuses `deriveTrackedChangeId` so structural and inline changes share one
 * metadata system. The author color is intentionally not set here; it is
 * stamped downstream by `stampTrackedChangeColors` in `@superdoc/contracts`.
 *
 * @param rowNode - PM table row node.
 * @param storyKey - Owning content story key, threaded onto the meta.
 * @returns The row's TrackedChangeMeta, or undefined when the row is untracked.
 */
const buildRowTrackedChangeMeta = (rowNode: PMNode, storyKey?: string): TrackedChangeMeta | undefined => {
  const trackChange = (rowNode.attrs as Record<string, unknown> | undefined)?.trackChange as
    | Record<string, unknown>
    | null
    | undefined;
  if (!trackChange || typeof trackChange !== 'object') return undefined;
  const rawType = trackChange.type;
  if (rawType !== 'rowInsert' && rawType !== 'rowDelete') return undefined;

  const kind = rawType === 'rowInsert' ? 'insert' : 'delete';
  const meta: TrackedChangeMeta = {
    kind,
    id: deriveTrackedChangeId(kind, trackChange),
  };
  if (typeof trackChange.author === 'string' && trackChange.author) {
    meta.author = trackChange.author;
  }
  if (typeof trackChange.authorEmail === 'string' && trackChange.authorEmail) {
    meta.authorEmail = trackChange.authorEmail;
  }
  if (typeof trackChange.date === 'string' && trackChange.date) {
    meta.date = trackChange.date;
  }
  if (typeof storyKey === 'string' && storyKey.length > 0) {
    meta.storyKey = storyKey;
  }
  return meta;
};

const parseTableRow = (args: ParseTableRowArgs): TableRow | null => {
  const { rowNode, rowIndex, context, defaultCellPadding, tableProperties, numRows } = args;
  if (!isTableRowNode(rowNode)) {
    return null;
  }

  const rowContent = Array.isArray(rowNode.content) ? rowNode.content : [];
  const cells: TableCell[] = [];
  const rowCnfStyle = (rowNode.attrs?.tableRowProperties as Record<string, unknown> | undefined)?.cnfStyle as
    | Record<string, unknown>
    | undefined;
  rowContent.forEach((cellNode, cellIndex) => {
    if (isTableCellNode(cellNode) && isTableSkipPlaceholderCell(cellNode)) {
      return;
    }

    const parsedCell = parseTableCell({
      cellNode,
      rowIndex,
      cellIndex,
      context,
      defaultCellPadding,
      tableProperties,
      numCells: rowContent.length || 1,
      numRows,
      rowCnfStyle,
      gridPlacement: args.cellGridPlacements?.[cellIndex] ?? null,
      numGridCols: args.numGridCols,
    });
    if (parsedCell) {
      cells.push(parsedCell);
    }
  });

  const rowProps = rowNode.attrs?.tableRowProperties;
  const rowHeight = normalizeRowHeight(rowProps as Record<string, unknown> | undefined);
  // Structural row-level tracked change (inserted/deleted whole row). Only
  // surfaced when tracked changes are enabled and not in 'off' mode, matching
  // how inline tracked-change metadata is gated (annotateBlockWithTrackedChange).
  const trackedChangesConfig = context.trackedChangesConfig;
  const rowTrackedChange =
    trackedChangesConfig?.enabled && trackedChangesConfig.mode !== 'off'
      ? buildRowTrackedChangeMeta(rowNode, context.storyKey)
      : undefined;

  // Row-level border override from w:tblPrEx/w:tblBorders (ECMA-376 §17.4.61).
  // The converter stores these raw (eighth-points) under tableRowProperties.tblPrExBorders.
  // tblPrEx overrides the table's borders for this row only; rows without it fall
  // through to the table borders (callout rows in FWC forms stay borderless this way).
  const tblPrExBordersRaw = (rowProps as Record<string, unknown> | undefined)?.tblPrExBorders;
  const rowBorders =
    tblPrExBordersRaw && typeof tblPrExBordersRaw === 'object'
      ? extractTableBorders(tblPrExBordersRaw as Record<string, unknown>, { unit: 'eighthPoints' })
      : undefined;

  const attrs: TableRowAttrs | undefined =
    rowProps && typeof rowProps === 'object'
      ? {
          tableRowProperties: rowProps as Record<string, unknown>,
          ...(rowHeight ? { rowHeight } : {}),
          ...(rowTrackedChange ? { trackedChange: rowTrackedChange } : {}),
          ...(rowBorders ? { borders: rowBorders } : {}),
        }
      : rowHeight || rowTrackedChange
        ? { ...(rowHeight ? { rowHeight } : {}), ...(rowTrackedChange ? { trackedChange: rowTrackedChange } : {}) }
        : undefined;

  // Note: cantSplit is stored within tableRowProperties.cantSplit (not as a separate attr)
  // The PM table-row extension has both cantSplit as a top-level attr AND within tableRowProperties
  // For layout engine, we only need to read from tableRowProperties.cantSplit

  return {
    id: context.nextBlockId(`row-${rowIndex}`),
    cells,
    attrs,
    sourceAnchor: sourceAnchorFromNode(rowNode),
  };
};

/**
 * Floating table properties from OOXML w:tblpPr.
 * Values are in twips.
 */
type FloatingTableProperties = {
  leftFromText?: number;
  rightFromText?: number;
  topFromText?: number;
  bottomFromText?: number;
  tblpX?: number;
  tblpY?: number;
  horzAnchor?: 'margin' | 'page' | 'text';
  vertAnchor?: 'margin' | 'page' | 'text';
  tblpXSpec?: 'left' | 'center' | 'right' | 'inside' | 'outside';
  tblpYSpec?: 'inline' | 'top' | 'center' | 'bottom' | 'inside' | 'outside';
};

function countFloatingTableContentColumns(node: PMNode): number {
  const grid = node.attrs?.grid;
  if (Array.isArray(grid) && grid.length > 0) {
    return grid.length;
  }

  const firstRow = node.content?.[0];
  if (!firstRow || !isTableRowNode(firstRow) || !Array.isArray(firstRow.content)) {
    return 0;
  }

  let columns = 0;
  for (const cellNode of firstRow.content) {
    if (!cellNode || !isTableCellNode(cellNode)) continue;
    const colSpan = typeof cellNode.attrs?.colspan === 'number' ? cellNode.attrs.colspan : 1;
    columns += colSpan > 0 ? colSpan : 1;
  }
  return columns;
}

/**
 * Extract floating table properties from node attrs and convert to TableAnchor and TableWrap.
 * Returns undefined values if the table is not floating (no tblpPr).
 */
function extractFloatingTableAnchorWrap(node: PMNode): { anchor?: TableAnchor; wrap?: TableWrap } {
  const tableProperties = node.attrs?.tableProperties as Record<string, unknown> | undefined;
  const floatingProps = tableProperties?.floatingTableProperties as FloatingTableProperties | undefined;

  if (!floatingProps) {
    return {};
  }

  // A table is considered anchored/floating if it has any positioning properties
  const hasPositioning =
    floatingProps.tblpX !== undefined ||
    floatingProps.tblpY !== undefined ||
    floatingProps.tblpXSpec !== undefined ||
    floatingProps.tblpYSpec !== undefined ||
    floatingProps.horzAnchor !== undefined ||
    floatingProps.vertAnchor !== undefined;

  if (!hasPositioning) {
    return {};
  }

  // Map OOXML anchor values to contract types
  const mapHorzAnchor = (val?: string): TableAnchor['hRelativeFrom'] => {
    switch (val) {
      case 'page':
        return 'page';
      case 'margin':
        return 'margin';
      case 'text':
      default:
        return 'column'; // 'text' in OOXML maps to column-relative positioning
    }
  };

  const mapVertAnchor = (val?: string): TableAnchor['vRelativeFrom'] => {
    switch (val) {
      case 'page':
        return 'page';
      case 'margin':
        return 'margin';
      case 'text':
      default:
        return 'paragraph'; // 'text' in OOXML maps to paragraph-relative positioning
    }
  };

  const anchor: TableAnchor = {
    isAnchored: true,
    hRelativeFrom: mapHorzAnchor(floatingProps.horzAnchor),
    vRelativeFrom: mapVertAnchor(floatingProps.vertAnchor),
  };

  // Set alignment from tblpXSpec/tblpYSpec if present
  if (floatingProps.tblpXSpec) {
    anchor.alignH = floatingProps.tblpXSpec;
  }
  if (floatingProps.tblpYSpec) {
    anchor.alignV = floatingProps.tblpYSpec;
  }

  // Set absolute offsets (convert twips to px)
  if (floatingProps.tblpX !== undefined) {
    anchor.offsetH = twipsToPx(floatingProps.tblpX);
  }
  if (floatingProps.tblpY !== undefined) {
    anchor.offsetV = twipsToPx(floatingProps.tblpY);
  }

  // Build wrap properties from text distances
  const hasHorizontalDistances = floatingProps.leftFromText !== undefined || floatingProps.rightFromText !== undefined;
  const hasVerticalDistances = floatingProps.topFromText !== undefined || floatingProps.bottomFromText !== undefined;
  const hasDistances = hasHorizontalDistances || hasVerticalDistances;

  const contentColumnCount = countFloatingTableContentColumns(node);
  // Single-cell form fields (Fair Work F3) are absolute overlays. Multi-column tables with
  // side distances (advanced-tables.docx) participate in square text wrapping.
  const isSingleCellHorizontalOverlay = contentColumnCount <= 1 && hasHorizontalDistances && !hasVerticalDistances;

  const wrap: TableWrap = {
    type: isSingleCellHorizontalOverlay ? 'None' : 'Square',
    wrapText: 'bothSides',
  };

  if (hasDistances) {
    if (floatingProps.topFromText !== undefined) {
      wrap.distTop = twipsToPx(floatingProps.topFromText);
    }
    if (floatingProps.bottomFromText !== undefined) {
      wrap.distBottom = twipsToPx(floatingProps.bottomFromText);
    }
    if (floatingProps.leftFromText !== undefined) {
      wrap.distLeft = twipsToPx(floatingProps.leftFromText);
    }
    if (floatingProps.rightFromText !== undefined) {
      wrap.distRight = twipsToPx(floatingProps.rightFromText);
    }
  }

  return { anchor, wrap };
}

/**
 * Convert a ProseMirror table node to a TableBlock
 *
 * @param node - Table node to convert
 * @param nextBlockId - Block ID generator
 * @param positions - Position map for PM node tracking
 * @param _styleContext - Style context (unused in current implementation)
 * @param trackedChanges - Optional tracked changes configuration
 * @param bookmarks - Optional bookmark position map
 * @param hyperlinkConfig - Hyperlink configuration
 * @param paragraphToFlowBlocks - Paragraph converter function (injected to avoid circular deps)
 * @returns TableBlock or null if conversion fails
 */
export function tableNodeToBlock(
  node: PMNode,
  {
    nextBlockId,
    positions,
    storyKey,
    trackedChangesConfig,
    bookmarks,
    hyperlinkConfig,
    themeColors,
    converterContext,
    converters,
    enableComments,
  }: TableNodeToBlockParams,
): FlowBlock | null {
  if (!Array.isArray(node.content) || node.content.length === 0) return null;
  const paragraphConverter = converters.paragraphToFlowBlocks;
  if (!paragraphConverter) return null;

  const parserDeps: TableParserDependencies = {
    nextBlockId,
    positions,
    storyKey,
    trackedChangesConfig,
    bookmarks,
    hyperlinkConfig,
    themeColors,
    converterContext,
    converters,
    enableComments,
  };

  // Compute the effective table style ID once per table. This single canonical
  // style ID is used for both table-level hydration and cell/paragraph cascades.
  const explicitStyleId = typeof node.attrs?.tableStyleId === 'string' ? node.attrs.tableStyleId : null;
  const resolvedStyle = resolveExistingTableEffectiveStyleId(explicitStyleId, converterContext?.translatedLinkedStyles);
  const effectiveStyleId = resolvedStyle.styleId;

  const hydratedTableStyle = hydrateTableStyleAttrs(node, converterContext, effectiveStyleId);
  const defaultCellPadding = hydratedTableStyle?.cellPadding;

  // Build tableProperties with the effective style ID for consistent cascade resolution.
  // PM node attrs are never mutated — the effective ID lives only in this transient object.
  // When effectiveStyleId is null (resolver found no style), strip any raw tableStyleId
  // from the cascade object to prevent invalid IDs from influencing resolution.
  const rawTableProperties = node.attrs?.tableProperties as TableProperties | undefined;
  const tablePropertiesForCascade: TableProperties | undefined =
    effectiveStyleId || rawTableProperties
      ? {
          ...rawTableProperties,
          tableStyleId: effectiveStyleId ?? undefined,
        }
      : undefined;

  const rows: TableRow[] = [];
  // Grid placements for conditional style regions (SD-3028 G7): Word's
  // firstCol/lastCol/banding follow grid columns, so each display cell needs
  // its grid start across rowspans, spans, and placeholder columns.
  const grid = node.attrs?.grid;
  const numGridCols = Array.isArray(grid) && grid.length > 0 ? grid.length : undefined;
  let activeRowSpans: number[] = [];
  node.content.forEach((rowNode, rowIndex) => {
    const { placements, nextActiveRowSpans } = placeRowCellsOnGrid(rowNode, activeRowSpans);
    activeRowSpans = nextActiveRowSpans;
    const parsedRow = parseTableRow({
      rowNode,
      rowIndex,
      numRows: node?.content?.length ?? 1,
      context: parserDeps,
      defaultCellPadding,
      tableProperties: tablePropertiesForCascade,
      cellGridPlacements: placements,
      numGridCols,
    });
    if (parsedRow) {
      // Drop a tracked row from the layout entirely (not just CSS-hide it in the
      // painter) when the current view mode removes it — inserted rows in
      // "original", deleted rows in "final" — so it never reserves blank table
      // space during measurement/pagination. Mirrors `shouldHideTrackedNode` for
      // inline content. If every row is hidden, the `rows.length === 0` guard
      // below omits the whole table block.
      if (!shouldHideTrackedNode(parsedRow.attrs?.trackedChange, parserDeps.trackedChangesConfig)) {
        rows.push(parsedRow);
      }
    }
  });

  if (rows.every((row) => row.cells.length === 0)) return null;

  const tableAttrs: Record<string, unknown> = {};

  const getBorderSource = (): { borders: Record<string, unknown>; unit: 'px' | 'eighthPoints' } | undefined => {
    if (
      node.attrs?.borders &&
      typeof node.attrs.borders === 'object' &&
      node.attrs.borders !== null &&
      Object.keys(node.attrs.borders as Record<string, unknown>).length > 0
    ) {
      return {
        borders: node.attrs.borders as Record<string, unknown>,
        unit: 'px',
      };
    }
    if (
      hydratedTableStyle?.borders &&
      typeof hydratedTableStyle.borders === 'object' &&
      hydratedTableStyle.borders !== null
    ) {
      return {
        borders: hydratedTableStyle.borders as Record<string, unknown>,
        unit: 'eighthPoints',
      };
    }
  };

  const borderSource = getBorderSource();
  const tableBorders: TableBorders | undefined = borderSource
    ? extractTableBorders(borderSource.borders, { unit: borderSource.unit })
    : undefined;
  if (tableBorders) tableAttrs.borders = tableBorders;

  if (node.attrs?.borderCollapse) {
    tableAttrs.borderCollapse = node.attrs.borderCollapse;
  }

  if (node.attrs?.tableCellSpacing !== undefined && node.attrs?.tableCellSpacing !== null) {
    tableAttrs.cellSpacing = normalizeCellSpacing(node.attrs.tableCellSpacing);
  } else if (hydratedTableStyle?.tableCellSpacing) {
    tableAttrs.cellSpacing = normalizeCellSpacing(hydratedTableStyle.tableCellSpacing);
    // Cell spacing requires border-collapse: separate
    if (!tableAttrs.borderCollapse) {
      tableAttrs.borderCollapse = 'separate';
    }
  }

  if (node.attrs?.justification) {
    tableAttrs.justification = node.attrs.justification;
  } else if (hydratedTableStyle?.justification) {
    tableAttrs.justification = hydratedTableStyle.justification;
  }

  if (node.attrs?.tableWidth) {
    tableAttrs.tableWidth = node.attrs.tableWidth;
  } else if (hydratedTableStyle?.tableWidth) {
    tableAttrs.tableWidth = hydratedTableStyle.tableWidth;
  }

  if (node.attrs?.tableIndent && typeof node.attrs.tableIndent === 'object') {
    tableAttrs.tableIndent = { ...node.attrs.tableIndent };
  } else if (hydratedTableStyle?.tableIndent) {
    tableAttrs.tableIndent = { ...hydratedTableStyle.tableIndent };
  }

  if (defaultCellPadding && typeof defaultCellPadding === 'object') {
    tableAttrs.defaultCellPadding = { ...defaultCellPadding };
  }

  const tableLayout = node.attrs?.tableLayout;
  if (tableLayout) {
    tableAttrs.tableLayout = tableLayout;
  } else if (hydratedTableStyle?.tableLayout) {
    tableAttrs.tableLayout = hydratedTableStyle.tableLayout;
  }

  // Preserve tableProperties for floating table detection and other OOXML metadata
  const tableProperties = node.attrs?.tableProperties;
  if (tableProperties && typeof tableProperties === 'object') {
    tableAttrs.tableProperties = tableProperties as Record<string, unknown>;
  }

  // SD-3171 Word-parity: `w:bidiVisual` visually flips cell order ONLY when
  // set inline on the table itself. Word does not visually flip when the only
  // source of `bidiVisual` is a style (verified empirically: a table style
  // carrying `w:bidiVisual` produces TableDirection=wdTableDirectionLtr and
  // renders cells in logical order in Word). The model-level cascade still
  // happens via style-engine; we just don't feed style-derived bidiVisual into
  // the painter's visual-direction signal.
  //
  // Normalize the rightToLeft / bidiVisual aliases on the inline layer
  // (importer normalizes w:bidiVisual to `rightToLeft`; aliases stay possible
  // when style-engine emits raw OOXML keys). `??` treats null/undefined as
  // missing but preserves an explicit `false`, so an inline
  // `<w:bidiVisual w:val="0"/>` is honored.
  const inlineProps = rawTableProperties as { rightToLeft?: boolean; bidiVisual?: boolean } | undefined;
  const inlineVisual = inlineProps?.rightToLeft ?? inlineProps?.bidiVisual;
  const effectiveForDirection = {
    rightToLeft: inlineVisual,
  };
  const sectionContext = converterContext?.sectionDirectionContext ?? resolveSectionDirection(undefined);
  tableAttrs.tableDirectionContext = resolveTableDirection(effectiveForDirection, sectionContext);

  let columnWidths: number[] | undefined = undefined;

  const twipsToPixels = (twips: number): number => {
    const PIXELS_PER_INCH = 96;
    return (twips / 1440) * PIXELS_PER_INCH;
  };

  /**
   * Column width priority hierarchy:
   * 1. User-edited grid (userEdited flag + grid attribute)
   * 2. Original OOXML grid (untouched documents — grid values sum to page width)
   * 3. PM colwidth attributes (fallback for PM-native edits or missing grid)
   * 4. Auto-calculate from content (no explicit widths)
   *
   * Grid values (from w:tblGrid) represent actual column positions on the page and
   * sum to exactly the content width. Cell colwidth values may be scaled up from tcW
   * (cell width hints) during import and require down-scaling in the measuring code,
   * which introduces proportion changes that make columns narrower than they should be.
   */

  // Priority 1: User-edited grid (preserves resize operations)
  const hasUserEditedGrid =
    node.attrs?.userEdited === true && Array.isArray(node.attrs?.grid) && node.attrs.grid.length > 0;

  if (hasUserEditedGrid) {
    columnWidths = (node.attrs!.grid as Array<{ col?: number } | null | undefined>)
      .filter((col): col is { col?: number } => col != null && typeof col === 'object')
      .map((col) => {
        const twips = typeof col.col === 'number' ? col.col : 0;
        return twips > 0 ? twipsToPixels(twips) : 0;
      })
      .filter((width: number) => width > 0);

    if (columnWidths.length === 0) {
      columnWidths = undefined;
    }
  }

  // Priority 2: Original OOXML grid (grid values are authoritative for column positions)
  if (!columnWidths && Array.isArray(node.attrs?.grid) && node.attrs.grid.length > 0) {
    columnWidths = (node.attrs.grid as Array<{ col?: number } | null | undefined>)
      .filter((col): col is { col?: number } => col != null && typeof col === 'object')
      .map((col) => {
        const twips = typeof col.col === 'number' ? col.col : 0;
        return twips > 0 ? twipsToPixels(twips) : 0;
      })
      .filter((width: number) => width > 0);

    if (columnWidths.length === 0) {
      columnWidths = undefined;
    }
  }

  // Priority 3: PM colwidth attributes (fallback when no grid is available)
  if (!columnWidths && Array.isArray(node.content) && node.content.length > 0) {
    const firstRow = node.content[0];
    if (firstRow && isTableRowNode(firstRow) && Array.isArray(firstRow.content) && firstRow.content.length > 0) {
      const tempWidths: number[] = [];
      for (const cellNode of firstRow.content) {
        if (cellNode && isTableCellNode(cellNode) && cellNode.attrs?.colwidth !== undefined) {
          const colwidth = cellNode.attrs.colwidth;
          if (Array.isArray(colwidth)) {
            tempWidths.push(...colwidth.filter((w) => typeof w === 'number' && w > 0));
          } else if (typeof colwidth === 'number' && colwidth > 0) {
            tempWidths.push(colwidth);
          }
        }
      }
      if (tempWidths.length > 0) {
        columnWidths = tempWidths;
      }
    }
  }

  // Priority 4: Auto-calculate from content (columnWidths remains undefined)

  // Extract floating table anchor/wrap properties
  const { anchor, wrap } = extractFloatingTableAnchorWrap(node);

  const tableBlock: TableBlock = {
    kind: 'table',
    id: nextBlockId('table'),
    rows,
    attrs: Object.keys(tableAttrs).length > 0 ? tableAttrs : undefined,
    columnWidths,
    ...(anchor ? { anchor } : {}),
    ...(wrap ? { wrap } : {}),
    sourceAnchor: sourceAnchorFromNode(node),
  };

  return tableBlock;
}

/**
 * Handle table nodes.
 * Converts table node to table block.
 *
 * @param node - Table node to process
 * @param context - Shared handler context
 */
export function handleTableNode(node: PMNode, context: NodeHandlerContext): void {
  const {
    blocks,
    recordBlockKind,
    nextBlockId,
    positions,
    trackedChangesConfig,
    bookmarks,
    hyperlinkConfig,
    converters,
    converterContext,
    enableComments,
  } = context;

  const tableBlock = tableNodeToBlock(node, {
    nextBlockId,
    positions,
    storyKey: context.storyKey,
    trackedChangesConfig,
    bookmarks,
    hyperlinkConfig,
    themeColors: undefined,
    converterContext,
    converters,
    enableComments,
  });
  if (tableBlock) {
    blocks.push(tableBlock);
    recordBlockKind?.(tableBlock.kind);
  }
}
